import { Typography, Box, CircularProgress } from '@mui/material';
import { useState, Dispatch, SetStateAction, MouseEvent } from 'react';
import { useNavigate } from "react-router-dom";

import { IssueRowPagination } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list-row-pagination';
import { JsmIssueBase, PaginationModel } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';

import styles from '../issue-list.module.scss';

import { AttachmentsCollapse } from './attachments-collapse/attachments-collapse';
import { CommentsCollapse } from './comments-collapse/comments-collapse';
import { DatesCollapse } from './dates-collapse/dates-collapse';
import { DescriptionCollapse } from './description-collapse/description-collapse';
import { DetailsCollapse } from './details-collapse/details-collapse';
import { IssueLinksCollapse } from './issue-links-collapse/issue-links-collapse';
import { PeopleCollapse } from './people-collapse/people-collapse';

interface IssueListViewProps {
  issue: JsmIssueBase | undefined;
  portalName: string;
  portalProject: string;
  portalId: string | undefined;
  total: number;
  headerIconUrl: string;
  placeholder: string;
  paginationDetails: {
    currIssueIndex: number;
    setCurrIssueIndex: Dispatch<SetStateAction<number>>;
    paginationModel: PaginationModel;
    setPaginationModel: (model: PaginationModel) => void;
    rowCount: number;
    pageSize: number;
    hasNextPage?: boolean;
    knownTotalPages?: number;
    isPaginationReady?: boolean;
  };
  loading: boolean;
}

export function IssueListView(props: IssueListViewProps) {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    details: true,
    description: true,
    attachments: true,
    issueLinks: true,
    activity: true,
    people: true,
    dates: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const issue = props.issue;
  const navigate = useNavigate();
  
  const isPaginationReady = props.paginationDetails?.isPaginationReady ?? false;

  const navigateToIssue = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`issues/${issue?.key}`, { state: props.issue });
  };

  if (props.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '90vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!issue) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '90vh',
          padding: '16px',
        }}
      >
        <Typography variant="h5" align="center">
          No issues found, change your search/filter.
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
          marginBottom: '16px',
          borderBottom: '1px solid #ccc',
          justifyContent: 'space-between', 
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={props.headerIconUrl}
            alt={issue.fields.issuetype.name}
            style={{
              width: '64px',
              height: '64px',
              marginRight: '16px',
            }}
          />
          <div>
            <Typography sx={{ marginBottom: '8px', fontSize: '14px' }}>
              {props.portalName + ' / '}
              <a href={`/browse/${props.portalProject}/issues/${issue?.key}`} onClick={navigateToIssue} className={styles['issue-link']}>
                {issue?.key}
              </a>
            </Typography>
            <Typography
              variant={'h4'}
              sx={{ marginBottom: '0px', fontWeight: 'bold' }}
            >
              {issue?.fields.summary}
            </Typography>
          </div>
        </div>

        <div style={{ paddingRight: '16px' }}>
          <IssueRowPagination
            currIssueIndex={props.paginationDetails.currIssueIndex}
            setCurrIssueIndex={props.paginationDetails.setCurrIssueIndex}
            paginationModel={props.paginationDetails.paginationModel}
            setPaginationModel={props.paginationDetails.setPaginationModel}
            rowCount={props.paginationDetails.rowCount}
            pageSize={props.paginationDetails.pageSize}
            hasNextPage={props.paginationDetails.hasNextPage}
            knownTotalPages={props.paginationDetails.knownTotalPages}
            loading={!isPaginationReady}
          />
        </div>
      </Box>

      {/* Collapsible sections */}
      <div style={{ display: 'flex', flexGrow: 1, padding: '16px' }}>
        <Box
          sx={{
            width: '70%',
            paddingRight: '16px',
          }}
        >
          {/* Details collapsible section */}
          {issue?.fields && (
            <DetailsCollapse
              issue={issue}
              expanded={expandedSections.details}
              toggleSection={() => toggleSection('details')}
              portalKey={props.portalId as string}
            />
          )}

          {/* Description collapsible section */}
          {issue?.fields.description?.content && (
            <DescriptionCollapse
              issue={issue}
              expanded={expandedSections.description}
              toggleSection={() => toggleSection('description')}
            />
          )}

          {/* Attachments collapsible section */}
          {issue?.fields.attachment?.length ? (
            <AttachmentsCollapse
              issue={issue}
              expanded={expandedSections.attachments}
              toggleSection={() => toggleSection('attachments')}
            />
          ) : null}

          {/* Issue links collapsible section */}
          {issue?.fields.issuelinks?.length ? (
            <IssueLinksCollapse
              issue={issue}
              expanded={expandedSections.issueLinks}
              toggleSection={() => toggleSection('issueLinks')}
            />
          ) : null}

          {/* Comments collapsible section */}
          <CommentsCollapse
            issue={issue}
            expanded={expandedSections.activity}
            toggleSection={() => toggleSection('activity')}
          />
        </Box>

        {/* Second column for People and Dates */}
        <Box
          sx={{
            width: '30%',
            paddingLeft: '16px',
            overflowY: 'auto',
          }}
        >
          {/* People Section */}
          <PeopleCollapse
            issue={issue}
            expanded={expandedSections.people}
            toggleSection={() => toggleSection('people')}
          />

          {/* Dates Section */}
          <DatesCollapse
            issue={issue}
            expanded={expandedSections.dates}
            toggleSection={() => toggleSection('dates')}
          />
        </Box>
      </div>
    </div>
  );
}